import { useCallback } from "react";
import {
  getApiBaseUrl,
  getApiBaseUrlFallback,
  isCorsOrNetworkError,
} from "../config/env";

const buildQueryString = (params) => {
  if (!params) return "";
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  if (entries.length === 0) return "";
  return new URLSearchParams(entries).toString();
};

const doFetch = async (baseUrl, path, query, method, body, headers, token, signal) => {
  const url = `${baseUrl}${path}${query ? `?${query}` : ""}`;
  return fetch(url, {
    method,
    headers: {
      accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
};

const useApi = () => {
  const baseUrl = getApiBaseUrl();
  const fallbackUrl = getApiBaseUrlFallback();

  const request = useCallback(
    async ({ path, method = "GET", params, body, headers, signal }) => {
      const primary = baseUrl || fallbackUrl;
      if (!primary) {
        throw new Error(
          "Missing API base URL. Set REACT_APP_API_BASE_URL_PROD (prod) or REACT_APP_API_BASE_URL_STG (staging)."
        );
      }

      const query = buildQueryString(params);
      const token = localStorage.getItem("userId");

      const tryRequest = async (base) => {
        const requestUrl = `${base}${path}${query ? `?${query}` : ""}`;
        const response = await doFetch(
          base,
          path,
          query,
          method,
          body,
          headers,
          token,
          signal
        );

      if (!response.ok) {
        // 401 (Unauthorized) means token is expired or invalid - should logout
        if (response.status === 401) {
          const message = await response.text();
          
          // Check if we just logged in (within last 10 seconds) - don't redirect immediately
          // This prevents redirect loops right after login when /user/me might fail initially
          const lastLoginTime = sessionStorage.getItem("lastLoginTime");
          const now = Date.now();
          const timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime, 10) : Infinity;
          
          // Only redirect if it's been more than 10 seconds since login
          // This gives the token time to be validated by the backend
          // Also check if we're already on the login page to avoid redirect loops
          const isOnLoginPage = window.location.pathname === "/login";
          
          if (timeSinceLogin > 10000 && !isOnLoginPage) {
            // Clear the token
            localStorage.removeItem("userId");
            // Clear the login time flag
            sessionStorage.removeItem("lastLoginTime");
            // Redirect to login page
            window.location.href = "/login";
            throw new Error(message || "Session expired. Please login again.");
          } else if (timeSinceLogin <= 10000) {
            // If we just logged in and got 401, don't clear token yet
            // This might be a timing issue - let the error propagate but don't redirect
            throw new Error(message || "Authentication failed. Please try again.");
          } else {
            // Normal case - clear token and redirect
            localStorage.removeItem("userId");
            sessionStorage.removeItem("lastLoginTime");
            if (!isOnLoginPage) {
              window.location.href = "/login";
            }
            throw new Error(message || "Session expired. Please login again.");
          }
        }
        
        // 403 (Forbidden) means user doesn't have permission - don't logout
        // Only logout on 403 if it's for /user/me endpoint (critical auth check)
        if (response.status === 403) {
          const message = await response.text();
          const isUserMeEndpoint = requestUrl.includes("/user/me");
          
          // Only clear token and redirect for /user/me endpoint (critical auth check)
          // For other endpoints, 403 is just a permission issue, not auth failure
          if (isUserMeEndpoint) {
            const isOnLoginPage = window.location.pathname === "/login";
            localStorage.removeItem("userId");
            sessionStorage.removeItem("lastLoginTime");
            if (!isOnLoginPage) {
              window.location.href = "/login";
            }
            throw new Error(message || "Access forbidden. Please login again.");
          } else {
            // For other endpoints, just throw error without clearing token
            throw new Error(message || "Access forbidden. You don't have permission to access this resource.");
          }
        }
        
        const message = await response.text();
        throw new Error(message || `Request failed with ${response.status}`);
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");
      
      // If content-length is 0, response is empty
      if (contentLength === "0") {
        return {};
      }
      
      // Try to read the response body
      try {
        const text = await response.text();
        
        // If text is empty, return empty object
        if (!text || !text.trim()) {
          return {};
        }
        
        // Only try to parse JSON if content-type indicates JSON or if text looks like JSON
        if (contentType?.includes("application/json") || text.trim().startsWith("{") || text.trim().startsWith("[")) {
          try {
            return JSON.parse(text);
          } catch (parseError) {
            // If JSON parsing fails, return empty object
            return {};
          }
        }
        
        // For non-JSON responses, return empty object
        return {};
      } catch (error) {
        // If reading response fails (e.g., empty body), return empty object
        return {};
      }
      };

      try {
        return await tryRequest(baseUrl || fallbackUrl);
      } catch (err) {
        if (
          fallbackUrl &&
          baseUrl &&
          baseUrl !== fallbackUrl &&
          isCorsOrNetworkError(err)
        ) {
          return await tryRequest(fallbackUrl);
        }
        throw err;
      }
    },
    [baseUrl, fallbackUrl]
  );

  return { request };
};

export default useApi;
